import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/brand";
import AppSwitcher from "@/components/AppSwitcher";
import {
  LayoutDashboard, GraduationCap, Mail, Compass, UserSearch, ClipboardCheck,
  BookOpen, Settings, LogOut, ShieldCheck, Sparkles, School,
} from "lucide-react";

const TRAINING_NAV = [
  { to: "/orientation", label: "New Teammate Orientation", icon: School, id: "orientation" },
  { to: "/trademark-classroom", label: "The Trademark Classroom", icon: GraduationCap, id: "trademark-classroom" },
  { to: "/knowledge", label: "Knowledge Base", icon: BookOpen, id: "knowledge" },
];

const HUB_NAV = [
  { to: "/bulletin", label: "The Bulletin", icon: LayoutDashboard, id: "bulletin" },
  { to: "/client-spotlight", label: "Client Spotlight", icon: UserSearch, id: "client-spotlight" },
  { to: "/reply-assistant", label: "Email Reply Assistant", icon: Mail, id: "reply-assistant" },
  { to: "/guidance-desk", label: "Client Guidance Desk", icon: Compass, id: "guidance-desk" },
  { to: "/review", label: "Review Pipeline", icon: ClipboardCheck, id: "review" },
];

const SectionLabel = ({ children, first }) => (
  <div className={`px-3 ${first ? "pt-1" : "pt-5"} pb-1.5 text-[10px] font-bold uppercase tracking-widest text-white/35`}>
    {children}
  </div>
);

const navClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
    isActive ? "bg-[#FFD100] text-[#141B4D] font-bold" : "text-white/75 hover:bg-white/10 hover:text-white"
  }`;

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const roleLabel = { teammate: "Teammate", reviewer: "Reviewer", attorney: "Attorney", admin: "Admin" }[user?.role];
  const fullAccess = user?.department === "fulfillment" || user?.role === "admin";
  const [showTools, setShowTools] = useState(() => localStorage.getItem("iu-show-tools") === "1");
  const toggleTools = () => {
    const nv = !showTools;
    setShowTools(nv);
    localStorage.setItem("iu-show-tools", nv ? "1" : "0");
  };
  const seeHub = fullAccess || showTools;

  return (
    <div className="min-h-screen flex bg-[#F2E9DB]">
      <aside className="w-64 shrink-0 bg-[#141B4D] text-white flex flex-col fixed h-screen">
        <div className="px-6 py-6 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#FFD100] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-[#141B4D]" />
            </div>
            <div>
              <div className="font-varsity leading-none text-2xl text-[#FFD100]">Indie U.</div>
              <div className="text-[11px] text-white/50 mt-1">Indie University</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {/* Training — visible to everyone */}
          <SectionLabel first>Training</SectionLabel>
          {TRAINING_NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.id}`} className={navClass}>
              <n.icon className="w-[18px] h-[18px]" /> {n.label}
            </NavLink>
          ))}

          {/* Day-to-Day Hub — full-access by default; non-fulfillment via the Explore toggle */}
          {seeHub && (
            <>
              <SectionLabel>Day-to-Day Hub</SectionLabel>
              {HUB_NAV.map((n) => (
                <NavLink key={n.to} to={n.to} data-testid={`nav-${n.id}`} className={navClass}>
                  <n.icon className="w-[18px] h-[18px]" /> {n.label}
                </NavLink>
              ))}
            </>
          )}

          {user?.role === "admin" && (
            <>
              <SectionLabel>Admin</SectionLabel>
              <NavLink to="/admin" data-testid="nav-admin" className={navClass}>
                <Settings className="w-[18px] h-[18px]" /> Admin
              </NavLink>
            </>
          )}

          {!fullAccess && (
            <button data-testid="toggle-fulfillment-tools" onClick={toggleTools}
              className="w-full mt-4 flex items-start gap-2.5 px-3 py-2.5 rounded-lg text-left text-xs text-white/70 bg-white/5 hover:bg-white/10 transition-all">
              <Sparkles className="w-4 h-4 text-[#FFD100] mt-0.5 shrink-0" />
              <span>{showTools ? "Hide the Day-to-Day Hub" : "Curious? Explore the Day-to-Day Hub"}</span>
            </button>
          )}
        </nav>

        <div className="px-3 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 mb-3">
            <Avatar name={user?.name} size={38} />
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                {user?.name}
                {user?.alumni && (
                  <span data-testid="alumni-badge" title="Indie University Alumni"
                    className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-[#FFD100] text-[#141B4D] rounded px-1.5 py-0.5">
                    <GraduationCap className="w-3 h-3" /> Alumni
                  </span>
                )}
              </div>
              <div className="text-[11px] text-white/50">{roleLabel} · {user?.title}</div>
            </div>
          </div>
          <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/70 hover:bg-white/10 hover:text-white transition-all">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-64 min-w-0 min-h-screen overflow-x-hidden">
        <AppSwitcher />
        {children}
      </main>
    </div>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#141B4D]">{title}</h1>
        {subtitle && <p className="text-[#101820]/70 mt-1.5 text-[15px] max-w-2xl">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}
